import React, { FocusEvent, KeyboardEvent } from "react";
import TextField from "@mui/material/TextField";
import { NumericFormat } from "react-number-format";

export interface RangeTextFieldProps {
  label: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInput: (e: React.ChangeEvent<HTMLInputElement>) => void; // Added onInput prop
  error: string;
  isDisabled: boolean;
  prefix?: string;
  thousandSeparator?: boolean | string;
  maxValue?: number;
}

/**
 * A reusable text field component for range inputs
 */
export const RangeTextField = ({
  label,
  placeholder,
  value,
  onChange,
  onInput, // Added onInput prop
  error,
  isDisabled,
  prefix = "",
  thousandSeparator = true,
  maxValue,
}: RangeTextFieldProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleValueChange = (values: any) => {
    const e = {
      target: {
        value: values.value,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onInput(e);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isAllowed = (values: any) => {
    const { floatValue } = values;

    // if maxValue is not defined or floatValue is not defined, allow any value
    if (maxValue === undefined || floatValue === undefined) {
      return true;
    }

    // otherwise, check if the value is within the max value
    return floatValue <= maxValue;
  };

  return (
    <NumericFormat
      customInput={TextField}
      size="small"
      label={label}
      placeholder={placeholder}
      variant="outlined"
      fullWidth
      value={value}
      onValueChange={handleValueChange}
      onBlur={(e: FocusEvent<HTMLInputElement>) => {
        const value = (e.target.value + "").replace(/,/g, ""); // Remove commas for numeric validation
        onChange({ target: { value } } as React.ChangeEvent<HTMLInputElement>);
      }}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
          const target = e.target as HTMLInputElement;
          const value = (target.value + "").replace(/,/g, ""); // Remove commas for numeric validation
          onChange({
            target: { value },
          } as React.ChangeEvent<HTMLInputElement>);
        }
      }}
      onChange={onInput} // Pass onInput as onChange handler
      error={!!error}
      helperText={error}
      disabled={isDisabled}
      thousandSeparator={thousandSeparator}
      prefix={prefix}
      allowNegative={false}
      slotProps={{
        inputLabel: {
          shrink: true,
        },
      }}
      decimalScale={0}
      valueIsNumericString
      isAllowed={isAllowed}
    />
  );
};
